<template>
  <div
    :class="[
      'article-head__other-fields',
      { 'article-head__other-fields--light': isFestivalYear },
    ]"
  >
    <!-- 文資身份、所在空間 -->
    <span
      :class="[
        'article-head__other-field',
        'article-head__other-field__identity',
        { 'article-head__other-field__identity--nested': isNested },
      ]"
      v-if="identity || space"
    >
      {{ identity }}
      <span v-if="identity && space" tabindex="-1">|</span>
      {{ space }}
    </span>

    <!-- 香科年活動日期：起始、結束 -->
    <div
      :class="[
        'article-head__other-field',
        'article-head__other-field__activity-date',
        { 'article-head__other-field__activity-date--nested': isNested },
      ]"
      v-if="startDate || endDate || lunarDate"
    >
      <span
        ><font-awesome-icon
          :icon="['fas', 'clock']"
          class="article-head__icon"
        />活動時間</span
      >
      <p v-if="startDate || endDate" class="article-head__other-field__item">
        <span class="article-head__other-field__subtitle">國曆</span>
        <span>
          <span v-if="startDate">
            {{ startDate }} ({{ getDay(startDate) }})
          </span>
          <span v-if="startDate && endDate">
            ～ {{ endDate }} ({{ getDay(endDate) }})
          </span>
        </span>
      </p>

      <p v-if="lunarDate" class="article-head__other-field__item">
        <span class="article-head__other-field__subtitle">農曆</span>
        <span>{{ lunarDate }} </span>
      </p>
    </div>

    <!-- 香科年文章類別 -->
    <div
      class="article-head__other-field article-head__other-field__activity-feature"
      v-if="activityFeature"
    >
      {{ activityFeature }}
    </div>

    <!-- 電話 -->
    <div
      class="article-head__other-field article-head__other-field__phone"
      v-if="telephone"
    >
      {{ telephone }}
    </div>

    <!-- 開放時間 -->
    <div
      class="article-head__other-field article-head__other-field__calendar"
      v-if="openingInfo"
    >
      {{ openingInfo }}
    </div>

    <!-- 地址 -->
    <div
      :class="[
        'article-head__other-field article-head__other-field__place',
        {
          'article-head__other-field__place--activity':
            isFestivalYear && template === 'activity',
        },
      ]"
      v-if="address"
    >
      <section v-if="isFestivalYear">
        <font-awesome-icon
          :icon="['fas', 'map-marker-alt']"
          class="article-head__icon"
        />活動地點
      </section>

      <p v-if="isFestivalYear" class="article-head__other-field__item">
        <a
          :href="`https://www.google.com.tw/maps/search/${address}`"
          target="_blank"
          :title="`[另開新分頁]前往${address}`"
          >{{ address }}</a
        >
      </p>
      <span v-else>{{ address }}</span>
    </div>

    <!-- 活動日期 -->
    <div
      class="article-head__other-field article-head__other-field__update-date"
      v-if="updateDate && isFestivalYear && template === 'information'"
    >
      {{ updateDate }}
    </div>

    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'ArticleHeadOtherFields',
  props: {
    identity: {
      type: String,
      default: '',
    },
    space: {
      type: String,
      default: '',
    },
    address: {
      type: String,
      default: '',
    },
    telephone: {
      type: String,
      default: '',
    },
    openingInfo: {
      type: String,
      default: '',
    },
    template: {
      type: String,
      default: 'information',
    },
    updateDate: {
      type: String,
      default: '',
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    lunarDate: {
      type: String,
      default: '',
    },
    activityFeature: {
      type: String,
      default: '',
    },
    isNested: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    isAllDataEmpty() {
      return (
        this.identity ||
        this.space ||
        this.address ||
        this.telephone ||
        this.openingInfo
      );
    },
    isFestivalYear() {
      return this.$route.meta.isRenderingFestivalTheme;
    },
  },
  methods: {
    getDay(date) {
      const dayList = ['日', '一', '二', '三', '四', '五', '六'];

      const theDay = new Date(date).getDay();

      return dayList[theDay];
    },
  },
  components: {},
};
</script>

<style lang="scss" scoped>
.article-head__icon {
  font-size: 0.95rem;
  margin-right: 5px;
  display: inline-block;
  vertical-align: middle;
  width: 15px;
}

.article-head__other-fields {
  position: relative;
  font-size: 1rem;
  color: $color-light-text;
  padding: 20px 0;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
  letter-spacing: 0.89px;
  width: calc(100% - 40px);

  @include for-tablet {
    width: calc(100% - 188px);
  }

  @include for-desktop {
    max-width: 1024px;
    min-height: 51px;
    margin-bottom: 36px;
  }

  > span,
  div {
    padding-left: 30px;

    &::before {
      content: "";
      position: absolute;
      display: inline-block;
      vertical-align: middle;
    }
  }

  > .article-head__other-field__calendar {
    @include for-desktop {
      display: block;
      margin-top: 0.75rem !important; // 桌機的開放時間間距
      margin-left: 0;
    }
  }

  &--light {
    width: calc(100% - 30px);
    background: none;
    border-bottom: 1px solid;
    padding: 0 0 15px;

    @include for-tablet {
      width: calc(100% - 188px);
    }

    @include for-tablet-horizontal {
      margin-bottom: 0;
    }

    @include for-desktop {
      max-width: 1024px;
      border-bottom: 0;
    }

    .article-head__other-field {
      padding: 10px 0 0;
      font-size: 0.875rem;
      letter-spacing: 0.78px;

      @include for-tablet {
        max-width: 320px;
        margin: auto;
      }
    }

    .article-head__other-field + .article-head__other-field {
      margin-top: 0;
    }
  }
}

.article-head__other-field {
  position: relative;
  font-weight: 600;

  @at-root .article-head__other-field + .article-head__other-field {
    margin-top: 10px;

    @include for-desktop {
      margin-top: 1.5rem;
    }
  }

  &__identity {
    display: inline-block;
    vertical-align: middle;
    @include truncate-string(95%);

    @include for-desktop {
      width: 100%;
    }

    &::before {
      width: 24px;
      height: 24px;
      left: -1px;
      bottom: -3px;
      background: url("../../assets/images/header/house_new.svg") left center
        no-repeat;
      background-size: contain;
    }

    &--nested {
      &::before {
        background: none;
      }
    }
  }

  &__place {
    &::before {
      width: 14px;
      height: 25px;
      left: 4px;
      top: -6px;
      background: url("../../assets/images/header/place.svg") center no-repeat;
      background-size: contain;
    }

    &--activity {
      &::before {
        background: none !important;
      }
    }
  }

  &__phone {
    @include for-desktop {
      margin-left: 30px;
    }

    &::before {
      width: 23px;
      height: 20px;
      left: -1px;
      bottom: -2px;
      background: url("../../assets/images/header/phone_new.svg") center
        no-repeat;
      background-size: contain;
    }
  }

  &__calendar {
    &::before {
      width: 18px;
      height: 20px;
      left: 2px;
      top: 0;
      background: url("../../assets/images/header/time.svg") center no-repeat;
      background-size: contain;
    }
  }

  &__place {
    &::before {
      width: 20px;
      height: 30px;
      left: 0;
      bottom: -6px;
      background: url("../../assets/images/header/place_new.svg") center
        no-repeat;
      background-size: contain;
    }
  }

  /* 以下為香科年文章欄位 */

  &__item {
    padding-left: 20px;
    margin-top: 5px;
    margin-bottom: 0;

    a {
      color: $color-light-text;
      text-decoration: underline;
      font-weight: 500;
    }
  }

  &__subtitle {
    margin-right: 5px;
    color: $color-light-link;
  }

  &__activity-date p {
    color: $color-light-text;
  }

  &__update-date {
    text-align: center;
    font-size: 0.875rem;

    @include for-tablet {
      font-size: 1rem !important;
    }
  }

  &__activity-feature {
    padding-left: 20px !important;

    &::before {
      width: 21px;
      height: 18px;
      left: -3px;
      bottom: -2px;
      background: url("../../assets/images/article/festival-year-activity-feature.svg")
        center no-repeat;
      background-size: contain;
    }
  }

  &__place {
    @include for-desktop {
      display: block !important;
    }
  }

  &__place,
  &__phone,
  &__calendar {
    font-size: 1rem;
    line-break: 1.5;

    @include for-desktop {
      display: inline-block;
      padding-bottom: 10px;
    }

    &::before {
      @include for-desktop {
        bottom: 8px;
      }
    }
  }
}
</style>
